import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import 'dayjs/locale/es'

dayjs.extend(relativeTime)
dayjs.extend(localizedFormat)
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.locale('es')

/**
 * Format file size in human readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}

/**
 * Format currency amount
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: GTQ)
 * @param {string} locale - Locale string (default: es-GT)
 * @returns {string} Formatted currency
 */
export const formatCurrency = (amount, currency = 'GTQ', locale = 'es-GT') => {
  const currencyMap = {
    'Q': 'GTQ',
    'QTZ': 'GTQ', // código que usa SAP B1 para quetzales
    'USD': 'USD',
    '$': 'USD'
  }

  const isoCurrency = currencyMap[currency] || currency

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: isoCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)
  } catch (error) {
    const symbolMap = {
      'Q': 'Q',
      'GTQ': 'Q',
      'QTZ': 'Q',
      'USD': '$',
      '$': '$'
    }

    const symbol = symbolMap[currency] || currency
    const formattedAmount = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)

    return `${symbol}${formattedAmount}`
  }
}

/**
 * Format number with locale-specific formatting
 * @param {number} number - Number to format
 * @param {string} locale - Locale string (default: es-GT)
 * @param {Object} options - Intl.NumberFormat options
 * @returns {string} Formatted number
 */
export const formatNumber = (number, locale = 'es-GT', options = {}) => {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    ...options
  }).format(number)
}

/**
 * Format date in various formats
 * @param {Date|string} date - Date to format
 * @param {string} format - Format string or preset
 * @returns {string} Formatted date
 */
export const formatDate = (date, format = 'short') => {
  if (!date) return ''

  let d
  if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
    d = dayjs(date + 'T12:00:00')
  } else {
    d = dayjs(date)
  }

  if (!d.isValid()) return 'Fecha inválida'

  switch (format) {
    case 'short':
      return d.format('DD/MM/YYYY')
    case 'medium':
      return d.format('DD MMM YYYY')
    case 'long':
      return d.format('DD [de] MMMM [de] YYYY')
    case 'datetime':
      return d.format('DD/MM/YYYY HH:mm')
    case 'time':
      return d.format('HH:mm')
    case 'iso':
      return d.toISOString()
    case 'relative':
      return d.fromNow()
    default:
      return d.format(format)
  }
}

/**
 * Format date relative to now (e.g., "hace 2 horas")
 * @param {Date|string} date - Date to format
 * @returns {string} Relative time string
 */
export const formatDistanceToNow = (date) => {
  if (!date) return ''
  
  const d = dayjs(date)
  if (!d.isValid()) return 'Fecha inválida'
  
  return d.fromNow()
}

/**
 * Format time duration in human readable format
 * @param {number} milliseconds - Duration in milliseconds
 * @returns {string} Formatted duration
 */
export const formatDuration = (milliseconds) => {
  if (!milliseconds || milliseconds < 0) return '0ms'
  
  const seconds = Math.floor(milliseconds / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  
  if (days > 0) {
    return `${days}d ${hours % 24}h`
  } else if (hours > 0) {
    return `${hours}h ${minutes % 60}m`
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`
  } else if (seconds > 0) {
    return `${seconds}s`
  } else {
    return `${milliseconds}ms`
  }
}

/**
 * Format percentage
 * @param {number} value - Value to format as percentage
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted percentage
 */
export const formatPercentage = (value, decimals = 1) => {
  return `${(value * 100).toFixed(decimals)}%`
}

/**
 * Truncate text with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text || text.length <= maxLength) return text || ''
  return `${text.substring(0, maxLength)}...`
}

/**
 * Capitalize first letter of each word
 * @param {string} text - Text to capitalize
 * @returns {string} Capitalized text
 */
export const capitalizeWords = (text) => {
  if (!text) return ''
  return text.replace(/\w\S*/g, (txt) => 
    txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  )
}

/**
 * Format phone number
 * @param {string} phone - Phone number to format
 * @param {string} format - Format pattern (default: Guatemala format)
 * @returns {string} Formatted phone number
 */
export const formatPhone = (phone, format = 'GT') => {
  if (!phone) return ''

  const digits = phone.replace(/\D/g, '')

  switch (format) {
    case 'GT':
      if (digits.length === 8) {
        return `${digits.slice(0, 4)}-${digits.slice(4)}`
      } else if (digits.length === 11 && digits.startsWith('502')) {
        return `+502 ${digits.slice(3, 7)}-${digits.slice(7)}`
      }
      break
    case 'US':
      if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
      }
      break
  }

  return phone
}

/**
 * Format tax ID (NIT in Guatemala)
 * @param {string} taxId - Tax ID to format
 * @returns {string} Formatted tax ID
 */
export const formatTaxId = (taxId) => {
  if (!taxId) return ''

  const clean = taxId.replace(/[^0-9A-Za-z]/g, '').toUpperCase()

  if (clean.length === 8 || clean.length === 9) {
    const main = clean.slice(0, -1)
    const check = clean.slice(-1)
    return `${main}-${check}`
  }
  
  return taxId
}

/**
 * Format invoice number with padding
 * @param {string|number} number - Invoice number
 * @param {number} padding - Minimum digits (default: 6)
 * @param {string} prefix - Prefix to add (default: 'FAC-')
 * @returns {string} Formatted invoice number
 */
export const formatInvoiceNumber = (number, padding = 6, prefix = 'FAC-') => {
  if (!number) return ''
  
  const numStr = number.toString()
  const paddedNumber = numStr.padStart(padding, '0')
  
  return `${prefix}${paddedNumber}`
}

/**
 * Parse and format address
 * @param {Object} address - Address object
 * @returns {string} Formatted address string
 */
export const formatAddress = (address) => {
  if (!address) return ''
  
  const parts = []
  
  if (address.street) parts.push(address.street)
  if (address.city) parts.push(address.city)
  if (address.state) parts.push(address.state)
  if (address.country) parts.push(address.country)
  if (address.zipCode) parts.push(address.zipCode)
  
  return parts.join(', ')
}

/**
 * Format status text with proper casing
 * @param {string} status - Status string
 * @returns {string} Formatted status
 */
export const formatStatus = (status) => {
  if (!status) return ''
  
  const statusMap = {
    pending: 'Pendiente',
    processing: 'Procesando',
    approved: 'Aprobada',
    rejected: 'Rechazada',
    in_sap: 'En SAP',
    invoice_canceled: 'Cancelada',
    pending_approval: 'Pendiente de Aprobación',
    uploaded: 'Subida',
    error: 'Error'
  }
  
  return statusMap[status] || capitalizeWords(status)
}

/**
 * Format validation error messages
 * @param {Object} errors - Validation errors object
 * @returns {string} Formatted error message
 */
export const formatValidationErrors = (errors) => {
  if (!errors || typeof errors !== 'object') return ''
  
  const messages = Object.values(errors).flat()
  return messages.join('. ')
}

export default {
  formatFileSize,
  formatCurrency,
  formatNumber,
  formatDate,
  formatDistanceToNow,
  formatDuration,
  formatPercentage,
  truncateText,
  capitalizeWords,
  formatPhone,
  formatTaxId,
  formatInvoiceNumber,
  formatAddress,
  formatStatus,
  formatValidationErrors
}

/**
 * Normaliza un correo antes de enviarlo al login.
 * Los teclados de celular agregan mayúsculas, espacios (también después del punto
 * o la arroba) y a veces caracteres invisibles o de ancho completo: con cualquiera
 * de ellos el correo ya no coincide con el registrado aunque en pantalla se vea igual.
 * @param {string} email
 * @returns {string}
 */
export const normalizeEmail = (email) => String(email ?? '')
  .normalize('NFKC')
  .replace(/[\u200B-\u200D\u2060\uFEFF]/g, '')
  .replace(/\s+/g, '')
  .toLowerCase()
